import { useCurrentFrame, interpolate, spring } from 'remotion';

// Constants
import { CLAMP_BOTH, VIDEO_CONFIG } from '@/constants';

export interface DiagonalWipeTransitionConfig {
  /** Frame when the transition should start */
  startFrame: number;
  /** Duration of the expand phase in frames (stripes expand to cover screen) */
  expandDuration?: number;
  /** Duration of the collapse phase in frames (stripes collapse to reveal next scene) */
  collapseDuration?: number;
  /** Duration to hold at full coverage */
  holdDuration?: number;
  /** Skew angle for diagonal stripes in degrees */
  skewAngle?: number;
  /** Custom easing function */
  easing?: (t: number) => number;
}

export interface DiagonalWipeTransitionResult {
  /** Overall progress of entire transition (0 to 1) */
  overallProgress: number;
  /** Progress of expand phase (0 to 1) */
  expandProgress: number;
  /** Progress of collapse phase (0 to 1) */
  collapseProgress: number;
  /** Top-left stripe width percentage */
  topLeftWidth: number;
  /** Bottom-right stripe width percentage */
  bottomRightWidth: number;
  /** Top-left stripe X translation during expand (moves outward) */
  topLeftExpandTranslateX: number;
  /** Bottom-right stripe X translation during expand (moves outward) */
  bottomRightExpandTranslateX: number;
  /** Top-left stripe X translation during collapse */
  topLeftCollapseTranslateX: number;
  /** Bottom-right stripe X translation during collapse */
  bottomRightCollapseTranslateX: number;
  /** Opacity of outgoing scene */
  outgoingOpacity: number;
  /** Opacity of incoming scene */
  incomingOpacity: number;
  /** Whether the transition has started */
  isTransitioning: boolean;
  /** Whether we're in the expand phase */
  isExpanding: boolean;
  /** Whether we're in the hold phase */
  isHolding: boolean;
  /** Whether we're in the collapse phase */
  isCollapsing: boolean;
  /** Whether the transition has completed */
  isComplete: boolean;
  /** The skew angle */
  skewAngle: number;
}

// Default configuration values
const DEFAULT_EXPAND_DURATION = 24; // ~0.8 seconds at 30fps (slightly longer for smoother effect)
const DEFAULT_COLLAPSE_DURATION = 24; // ~0.8 seconds at 30fps
const DEFAULT_HOLD_DURATION = 6; // ~0.2 seconds at 30fps
const DEFAULT_SKEW_ANGLE = -50; // Matches existing diagonal stripes
// Ease-out cubic for collapse - starts at full speed, slows at end
const DEFAULT_EASING = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Creates a diagonal wipe transition effect between two scenes in Remotion.
 *
 * This hook calculates the animation values needed to expand diagonal stripes
 * from Scene3b to cover the entire screen, then collapse them to reveal Scene4.
 * The stripes expand BIDIRECTIONALLY - both inward (toward center) and outward
 * (toward edges) simultaneously for a more dynamic, professional effect.
 *
 * Animation phases:
 * 1. EXPAND: Diagonal stripes expand from their initial positions in BOTH directions
 * 2. HOLD: Full white screen is held briefly
 * 3. COLLAPSE: Stripes collapse/slide away to reveal the next scene
 *
 * @param config - Configuration object for the transition
 */
export const useDiagonalWipeTransition = ({
  startFrame,
  expandDuration = DEFAULT_EXPAND_DURATION,
  collapseDuration = DEFAULT_COLLAPSE_DURATION,
  holdDuration = DEFAULT_HOLD_DURATION,
  skewAngle = DEFAULT_SKEW_ANGLE,
}: DiagonalWipeTransitionConfig): DiagonalWipeTransitionResult => {
  const frame = useCurrentFrame();
  const { fps, width } = VIDEO_CONFIG;

  const totalDuration = expandDuration + holdDuration + collapseDuration;

  // Phase timing
  const expandEnd = startFrame + expandDuration;
  const holdEnd = expandEnd + holdDuration;
  const transitionEnd = startFrame + totalDuration;

  // Use spring for smoother expand animation
  const expandSpring = spring({
    frame: frame - startFrame,
    fps,
    config: {
      damping: 100,
      stiffness: 80,
      mass: 0.8,
    },
    durationInFrames: expandDuration,
  });

  // Calculate phase progress values
  const expandProgress = interpolate(expandSpring, [0, 1], [0, 1], CLAMP_BOTH);

  // Use frame-based interpolation with easing for collapse
  // This guarantees the animation reaches 1.0 and stripes slide out completely
  const collapseProgress = interpolate(
    frame,
    [holdEnd, transitionEnd],
    [0, 1],
    {
      ...CLAMP_BOTH,
      easing: DEFAULT_EASING, // Ease out cubic - starts fast, slows at end
    },
  );

  const overallProgress = interpolate(
    frame,
    [startFrame, transitionEnd],
    [0, 1],
    CLAMP_BOTH,
  );

  // Phase detection
  const isExpanding = frame >= startFrame && frame < expandEnd;
  const isHolding = frame >= expandEnd && frame < holdEnd;
  const isCollapsing = frame >= holdEnd && frame < transitionEnd;
  const isTransitioning = frame >= startFrame && frame < transitionEnd;
  const isComplete = frame >= transitionEnd;

  // Stripe width animation - expand from both sides
  // During expand: stripes grow from initial width to cover full screen
  // During collapse: stripes maintain full width while sliding out

  // Calculate expanded width needed to cover screen (accounting for skew)
  const skewFactor = Math.abs(Math.tan((skewAngle * Math.PI) / 180));
  const expandedWidth = 50 + skewFactor * 60; // Extra width to ensure full coverage

  // Initial width matches Scene3b stripes
  const initialWidth = 34;

  // Width expands from 34% to full coverage
  const topLeftWidth = interpolate(
    expandProgress,
    [0, 1],
    [initialWidth, expandedWidth],
    CLAMP_BOTH,
  );

  const bottomRightWidth = interpolate(
    expandProgress,
    [0, 1],
    [initialWidth, expandedWidth],
    CLAMP_BOTH,
  );

  // BIDIRECTIONAL expansion translation
  // Stripes move OUTWARD while also expanding their width
  // This creates the effect of growing from both edges simultaneously

  // Top-left stripe moves LEFT (outward) during expand
  const topLeftExpandTranslateX = interpolate(
    expandProgress,
    [0, 1],
    [0, -300], // Move 300px to the left
    CLAMP_BOTH,
  );

  // Bottom-right stripe moves RIGHT (outward) during expand
  const bottomRightExpandTranslateX = interpolate(
    expandProgress,
    [0, 1],
    [0, 300], // Move 300px to the right
    CLAMP_BOTH,
  );

  // Collapse translation - stripes slide out completely
  // Need very large values to ensure stripes fully exit (accounting for 120%+ width, skew angle, and expand offset)
  const collapseDistance = width * 2.5 + 800; // 2.5x viewport width + larger buffer

  // Top-left stripe slides out to the left
  const topLeftCollapseTranslateX = interpolate(
    collapseProgress,
    [0, 1],
    [0, -collapseDistance],
    CLAMP_BOTH,
  );

  // Bottom-right stripe slides out to the right
  const bottomRightCollapseTranslateX = interpolate(
    collapseProgress,
    [0, 1],
    [0, collapseDistance],
    CLAMP_BOTH,
  );

  // Scene opacity transitions
  // Outgoing scene fades during expand phase
  const outgoingOpacity = interpolate(
    expandProgress,
    [0.4, 0.9],
    [1, 0],
    CLAMP_BOTH,
  );

  // Incoming scene appears during collapse phase
  const incomingOpacity = interpolate(
    collapseProgress,
    [0, 0.4],
    [0, 1],
    CLAMP_BOTH,
  );

  return {
    overallProgress,
    expandProgress,
    collapseProgress,
    topLeftWidth,
    bottomRightWidth,
    topLeftExpandTranslateX,
    bottomRightExpandTranslateX,
    topLeftCollapseTranslateX,
    bottomRightCollapseTranslateX,
    outgoingOpacity,
    incomingOpacity,
    isTransitioning,
    isExpanding,
    isHolding,
    isCollapsing,
    isComplete,
    skewAngle,
  };
};
